import { Box, Typography, Stack, debounce, StackProps } from '@mui/material';
import ReactECharts from 'echarts-for-react';
import { useEffect, useMemo, useState } from 'react';
import { useTheme } from '@mui/material/styles';
import WidgetWraper from 'components/Common/WidgetWraper';
import LoaderComponent from 'components/Loader';
import HighlightedText from 'components/GlobalSearch/HighlightText';
import { InfoTooltip } from '../InfoTooltip';
import { buildRaceBarChartOptions } from '../chartWidgetUtility';

type SeriesConfig = {
  key: string;
  name: string;
  color?: string;
};

interface RaceBarChartProps {
  title: any;
  subTitle?: string;
  isLoading?: boolean;
  data: any[];
  containerProps?: StackProps;
  series: SeriesConfig[];
  tooltipLabel?: any;
  hasTooltip?: boolean;
  showGridLines?: boolean;
  tooltipFormatter?: (params: any) => string;
  valueType?: 'cost' | 'storage' | 'number';
  storageUnit?: 'KB' | 'MB' | 'GB' | 'TB' | 'PB';
  onBarClick?: (params: any) => void;
  xAxisLabelFormatter?: (value: number) => string;
}

const RaceBarChart = ({
  title,
  subTitle,
  isLoading = false,
  data,
  containerProps,
  series,
  tooltipLabel,
  hasTooltip = true,
  showGridLines = false,
  tooltipFormatter,
  valueType = 'number',
  storageUnit = 'GB',
  onBarClick,
  xAxisLabelFormatter,
}: RaceBarChartProps) => {
  const theme = useTheme();
  const colors = theme.palette;

  const [windowSize, setWindowSize] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 0
  );

  const chartData = useMemo(() => data ?? [], [data]);

  const seriesColors = useMemo(
    () =>
      series.reduce<Record<string, string>>((accumulator, currentSeries) => {
        if (currentSeries.color) {
          accumulator[currentSeries.key] = currentSeries.color;
        }

        return accumulator;
      }, {}),
    [series]
  );

  const options = useMemo(() => {
    const chartOptions: any = buildRaceBarChartOptions({
      data: chartData,
      colors,
      showGridLines,
      tooltipFormatter,
      stackSeries: series,
      seriesColors,
      valueType,
      storageUnit,
    });

    if (!chartOptions || !xAxisLabelFormatter) {
      return chartOptions;
    }

    const applyXAxisFormatter = (xAxis: any) => ({
      ...xAxis,
      axisLabel: {
        ...xAxis?.axisLabel,
        formatter: xAxisLabelFormatter,
      },
    });

    return {
      ...chartOptions,
      xAxis: Array.isArray(chartOptions.xAxis)
        ? chartOptions.xAxis.map(applyXAxisFormatter)
        : applyXAxisFormatter(chartOptions.xAxis),
    };
  }, [
      chartData,
      colors,
      showGridLines,
      tooltipFormatter,
      series,
      seriesColors,
      valueType,
      storageUnit,
      xAxisLabelFormatter,
    ]
  );

  const chartEvents = useMemo(
    () => ({
      click: (params: any) => {
        onBarClick?.(params);
      },
    }),
    [onBarClick]
  );

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleResize = debounce(() => {
      setWindowSize(window.innerWidth);
    }, 200);

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      handleResize.clear();
    };
  }, []);

  const renderChartContent = () => {
    if (isLoading) {
      return <LoaderComponent pt={20} />;
    }

    if (!chartData.length || !options) {
      return (
        <Stack
          height="100%"
          alignItems="center"
          justifyContent="center"
        >
          <Typography
            variant="bodyText"
            sx={{ color: colors.widget.text.main }}
          >
            No Data Available
          </Typography>
        </Stack>
      );
    }

    return (
      <ReactECharts
        option={options}
        style={{ width: '100%', height: '100%' }}
        onEvents={chartEvents}
      />
    );
  };

  return (
    <WidgetWraper>
      <Box
        sx={{
          backgroundColor: `${colors.widget.background.card} !important`,
        }}
        {...containerProps}
      >
        <Stack gap={2}>
          <Stack direction="row" alignItems="center">
            <Typography
              variant="widgetTitleNew"
              sx={{ color: colors.widget.text.title }}
            >
              <HighlightedText text={title} />
            </Typography>

            {hasTooltip && tooltipLabel && (
              <InfoTooltip label={tooltipLabel} />
            )}
          </Stack>

          {subTitle && (
            <Typography
              variant="bodyText"
              sx={{ color: colors.widget.text.title }}
            >
              {subTitle}
            </Typography>
          )}
        </Stack>

        <Box mt={2} height={350} key={windowSize}>
          {renderChartContent()}
        </Box>
      </Box>
    </WidgetWraper>
  );
};

export default RaceBarChart;